import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errText } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, MailCheck, ArrowLeft } from 'lucide-react';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      toast.error(errText(err, 'Could not process the request. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen hero-wash">
      <div className="noise min-h-screen flex flex-col">
        <header className="h-14 flex items-center px-4 lg:px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="forgot-logo">
            <div className="h-7 w-7 rounded-lg bg-primary text-primary-foreground grid place-items-center font-bold">M</div>
            <span className="font-semibold tracking-tight">DoctorPaperwork</span>
          </Link>
        </header>
        <div className="flex-1 grid place-items-center px-4 py-10">
          <Card className="w-full max-w-md shadow-lg shadow-black/5" data-testid="forgot-password-card">
            {sent ? (
              <CardContent className="pt-6 text-center space-y-3">
                <div className="mx-auto h-12 w-12 rounded-full bg-accent text-accent-foreground grid place-items-center">
                  <MailCheck className="h-6 w-6" />
                </div>
                <h2 className="text-lg font-semibold" data-testid="forgot-success-message">Check your inbox</h2>
                <p className="text-sm text-muted-foreground">
                  If an account exists for <span className="font-medium text-foreground">{email}</span>, a password reset link is on its way. It expires in 60 minutes.
                </p>
                <p className="text-xs text-muted-foreground">
                  Didn't get it? Check spam, or ask your practice admin to generate a reset link for you from the Users page.
                </p>
                <Button asChild variant="outline" className="mt-2">
                  <Link to="/login" data-testid="forgot-back-to-login"><ArrowLeft className="h-4 w-4 mr-1" /> Back to sign in</Link>
                </Button>
              </CardContent>
            ) : (
              <>
                <CardHeader>
                  <CardTitle className="text-xl">Reset your password</CardTitle>
                  <CardDescription>Enter your work email and we'll send you a reset link.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={submit} className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="forgot-email" className="text-xs font-medium">Work email</Label>
                      <Input id="forgot-email" type="email" required autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourpractice.com" data-testid="forgot-email-input" />
                    </div>
                    <Button type="submit" className="w-full" disabled={busy} data-testid="forgot-submit-button">
                      {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send reset link
                    </Button>
                  </form>
                  <p className="mt-4 text-sm text-muted-foreground">
                    Remembered it?{' '}
                    <Link to="/login" className="text-primary font-medium hover:underline" data-testid="forgot-login-link">Back to sign in</Link>
                  </p>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
